import { useCallback, useContext, useEffect, useRef } from 'react';
import { Link, UNSAFE_NavigationContext as NavigationContext, Route, Routes, matchPath, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import NavLink from '../NavLink';
const useConfirmExit = (confirmExit, when = true, locks)=>{
    const { navigator } = useContext(NavigationContext);
    useEffect(()=>{
        if (when) {
            const push = navigator.push;
            navigator.push = (...args)=>{
                const result = confirmExit(...args);
                if (result !== false) {
                    locks.current.forEach((release)=>{
                        release();
                    });
                    locks.current = [];
                    push(...args);
                }
            };
            return ()=>{
                navigator.push = push;
            };
        }
    }, [
        navigator,
        confirmExit,
        when,
        locks
    ]);
};
/**
 * Alternative hook for missing Prompt in React Router v6
 *
 * @param {Function} message Not message, but function which is called for decision when block navigation
 * @param {Boolean} when When block navigation
 */ export const usePrompt = (message, when)=>{
    const { navigator } = useContext(NavigationContext);
    const locks = useRef([]);
    useEffect(()=>{
        if (when && navigator.block) {
            const release = navigator.block(message);
            locks.current.push(release);
            return ()=>{
                release();
            };
        } else if (when && !navigator.block) {
            window.onbeforeunload = ()=>{
                return message();
            };
            return ()=>{
                window.onbeforeunload = null;
            };
        }
    }, [
        message,
        when,
        navigator
    ]);
    const confirmExit = useCallback((nextLocation)=>{
        return message(nextLocation);
    }, [
        message
    ]);
    useConfirmExit(confirmExit, when, locks);
};
const useRemoveQueryParams = ()=>{
    const [searchParams, setSearchParams] = useSearchParams();
    return useCallback((...removeParams)=>{
        removeParams.forEach((param)=>{
            if (searchParams.has(param)) {
                searchParams.delete(param);
            }
        });
        setSearchParams(searchParams, {
            replace: true
        });
    }, [
        searchParams,
        setSearchParams
    ]);
};
const useRouteChanged = (handleRouteChange)=>{
    const location = useLocation();
    useEffect(()=>{
        handleRouteChange({
            pathname: location.pathname,
            search: location.search
        });
    }, [
        location,
        handleRouteChange
    ]);
};
const Prompt = ({ when, message })=>{
    usePrompt(message, when);
    return null;
};
export const defaultSlice = {
    routing: {
        Router: {
            usePush: useNavigate,
            useLocation,
            NavLink,
            Link,
            Route,
            Switch: Routes,
            Prompt,
            matchPath,
            useRemoveQueryParams,
            useRouteChanged
        }
    }
};
